import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../../components/Header';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { FileDown, Settings, Mail, Send, Search } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const STATUS_COLORS = { pending: 'bg-yellow-100 text-yellow-800', paid: 'bg-blue-100 text-blue-800', shipped: 'bg-purple-100 text-purple-800', delivered: 'bg-green-100 text-green-800', refunded: 'bg-red-100 text-red-800', cancelled: 'bg-gray-200 text-gray-700' };

const AdminInvoices = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({}); // { order_id: true }
  const [search, setSearch] = useState('');
  const [bulkSending, setBulkSending] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { fetchOrders(); }, []);

  const fetchOrders = async () => {
    try {
      const { data } = await axios.get(`${API}/api/orders`, { withCredentials: true });
      setOrders(data.filter(o => o.status !== 'pending'));
    } catch { toast.error('Failed to load orders'); }
    finally { setLoading(false); }
  };

  const downloadInvoice = async (orderId) => {
    try {
      const response = await axios.get(`${API}/api/invoice/${orderId}`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${orderId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download invoice'); }
  };

  const verifyInvoice = (orderId) => {
    // Open the PDF in a new tab so the admin can visually verify it before sending.
    window.open(`${API}/api/invoice/${orderId}`, '_blank', 'noopener');
  };

  const sendOne = async (orderId) => {
    try {
      const { data } = await axios.post(`${API}/api/orders/${orderId}/email-invoice`, {}, { withCredentials: true });
      toast.success(data.message || 'Invoice emailed');
      fetchOrders();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to email invoice'); }
  };

  const filteredOrders = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return orders;
    return orders.filter((o) => (
      o.order_id.toLowerCase().includes(q) ||
      (o.shipping_address?.name || '').toLowerCase().includes(q) ||
      (o.guest_email || '').toLowerCase().includes(q)
    ));
  }, [orders, search]);

  const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);
  const allFilteredSelected = filteredOrders.length > 0 && filteredOrders.every((o) => selected[o.order_id]);
  const toggleAll = () => {
    if (allFilteredSelected) {
      const next = { ...selected };
      filteredOrders.forEach((o) => { delete next[o.order_id]; });
      setSelected(next);
    } else {
      const next = { ...selected };
      filteredOrders.forEach((o) => { next[o.order_id] = true; });
      setSelected(next);
    }
  };
  const toggleOne = (orderId) => setSelected((s) => ({ ...s, [orderId]: !s[orderId] }));

  const bulkSend = async () => {
    if (selectedIds.length === 0) { toast.error('Select at least one invoice to email'); return; }
    if (!window.confirm(`Email ${selectedIds.length} invoice PDF${selectedIds.length === 1 ? '' : 's'} to the recorded customer email${selectedIds.length === 1 ? '' : 's'}?`)) return;
    setBulkSending(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/orders/email-invoices-bulk`, { order_ids: selectedIds }, { withCredentials: true });
      const sent = data.summary?.sent || 0;
      const failed = data.summary?.failed || 0;
      if (failed === 0) toast.success(`All ${sent} invoice${sent === 1 ? '' : 's'} emailed.`);
      else toast.warning(`${sent} sent · ${failed} failed. ${data.failed?.[0]?.reason ? '(' + data.failed[0].reason + ')' : ''}`);
      setSelected({});
      fetchOrders();
    } catch (err) { toast.error(err.response?.data?.detail || 'Bulk email failed'); }
    finally { setBulkSending(false); }
  };

  if (loading) {
    return (<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>);
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      <div className="container mx-auto px-4 py-10 pt-16">
        <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
          <h1 className="text-3xl font-black font-heading" data-testid="admin-invoices-heading">Invoices</h1>
          <Button variant="outline" size="sm" onClick={() => navigate('/admin/invoice-settings')} data-testid="invoice-settings-link">
            <Settings className="h-4 w-4 mr-1" /> Template Settings
          </Button>
        </div>

        <div className="flex flex-wrap gap-2 mb-4 items-center">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search by order ID, customer, guest email" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="invoice-search-input" />
          </div>
          <div className="text-xs text-muted-foreground" data-testid="selected-count">
            {selectedIds.length} selected
          </div>
          <Button onClick={bulkSend} disabled={bulkSending || selectedIds.length === 0} data-testid="bulk-email-button">
            <Send className="h-4 w-4 mr-1" /> {bulkSending ? 'Emailing…' : `Email Selected (${selectedIds.length})`}
          </Button>
        </div>

        {filteredOrders.length === 0 ? (
          <div className="text-center py-20"><p className="text-muted-foreground" data-testid="no-invoices-message">No invoices match your search</p></div>
        ) : (
          <div className="rounded-xl border border-[#E0E0E0] bg-white overflow-hidden">
            <table className="w-full text-sm" data-testid="invoices-table">
              <thead>
                <tr className="border-b border-[#E0E0E0] bg-muted">
                  <th className="p-3 w-10">
                    <input type="checkbox" checked={allFilteredSelected} onChange={toggleAll} aria-label="Select all" data-testid="select-all-checkbox" />
                  </th>
                  <th className="text-left p-3 font-semibold text-xs uppercase tracking-wider">Invoice #</th>
                  <th className="text-left p-3 font-semibold text-xs uppercase tracking-wider">Order ID</th>
                  <th className="text-left p-3 font-semibold text-xs uppercase tracking-wider">Customer</th>
                  <th className="text-left p-3 font-semibold text-xs uppercase tracking-wider">Email</th>
                  <th className="text-left p-3 font-semibold text-xs uppercase tracking-wider">Date</th>
                  <th className="text-right p-3 font-semibold text-xs uppercase tracking-wider">Amount</th>
                  <th className="text-center p-3 font-semibold text-xs uppercase tracking-wider">Status</th>
                  <th className="text-right p-3 font-semibold text-xs uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((order, idx) => {
                  const customerEmail = order.guest ? (order.guest_email || '') : '';
                  const sentAlready = !!order.invoice_emailed_at;
                  return (
                    <tr key={order.order_id} className="border-b border-[#E0E0E0] last:border-0 hover:bg-muted/30" data-testid={`invoice-row-${order.order_id}`}>
                      <td className="p-3 text-center">
                        <input type="checkbox" checked={!!selected[order.order_id]} onChange={() => toggleOne(order.order_id)} aria-label={`Select ${order.order_id}`} data-testid={`select-${order.order_id}`} />
                      </td>
                      <td className="p-3 font-medium">INV-{String(idx + 1).padStart(4, '0')}</td>
                      <td className="p-3 text-muted-foreground text-xs">{order.order_id}</td>
                      <td className="p-3">
                        {order.shipping_address?.name || (order.guest ? order.guest_name : order.user_id)}
                        {order.guest && <span className="ml-2 text-[10px] uppercase tracking-wide bg-gray-100 text-gray-600 rounded px-1.5 py-0.5">Guest</span>}
                      </td>
                      <td className="p-3 text-muted-foreground text-xs">
                        {customerEmail || (order.guest ? '—' : <span className="text-muted-foreground">on file</span>)}
                        {sentAlready && <span className="ml-2 text-[10px] uppercase font-bold tracking-wide bg-green-50 text-green-700 rounded px-1.5 py-0.5" title={`Sent at ${new Date(order.invoice_emailed_at).toLocaleString('en-IN')}`}>Sent</span>}
                      </td>
                      <td className="p-3 text-muted-foreground">{new Date(order.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}</td>
                      <td className="p-3 text-right font-bold text-primary">{'\u20B9'}{order.total_amount.toFixed(2)}</td>
                      <td className="p-3 text-center"><span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${STATUS_COLORS[order.status] || 'bg-gray-100 text-gray-800'}`}>{order.status}</span></td>
                      <td className="p-3 text-right">
                        <div className="inline-flex gap-1">
                          <Button size="sm" variant="outline" className="rounded-full text-xs h-7 px-2" onClick={() => verifyInvoice(order.order_id)} data-testid={`verify-invoice-${order.order_id}`}>
                            Verify
                          </Button>
                          <Button size="sm" variant="outline" className="rounded-full text-xs h-7 px-2" onClick={() => downloadInvoice(order.order_id)} data-testid={`download-invoice-${order.order_id}`}>
                            <FileDown className="h-3 w-3 mr-1" /> PDF
                          </Button>
                          <Button size="sm" className="rounded-full text-xs h-7 px-2" onClick={() => sendOne(order.order_id)} data-testid={`send-invoice-${order.order_id}`}>
                            <Mail className="h-3 w-3 mr-1" /> Email
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminInvoices;
